import { isDefined, isNil } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
  WorkplaceShiftRateNegotiability,
} from "@src/appV2/OpenShifts/RateNegotiation/types";
import { BookabilityUnmetCriteria } from "@src/appV2/OpenShifts/ShiftAction";
import {
  hasAdditionalUnmetBookabilityCriteria,
  hasUnmetBookabilityCriteria,
} from "@src/appV2/OpenShifts/ShiftAction/hasUnmetBookabilityCriteria";
import { type BookabilityStatusItem } from "@src/appV2/OpenShifts/ShiftAction/types";

import { useDefinedWorker } from "../../../Worker/useDefinedWorker";
import { useShiftModalsDataContext } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { useGetBookingRequests } from "../../WorkerShifts/BookingRequests/api/useGetBookingRequests";
import { useWorkplaceHasBookingRequestsEnabled } from "../../WorkerShifts/BookingRequests/api/useWorkplaceHasBookingRequestsEnabled";
import { BookingRequestStatus } from "../../WorkerShifts/BookingRequests/types";
import { type BlockingRequirementsContext } from "../Bookability/Documents/types";
import { isBookabilityAllowedWithBackgroundCheck } from "../Bookability/isBookabilityAllowedWithBackgroundCheck";
import { getBackgroundCheckContext } from "../getBackgroundCheckContext";
import { isShiftRateNegotiationEnabled } from "../RateNegotiation/isShiftRateNegotiationEnabled";
import { useShiftRateNegotiationConditions } from "../RateNegotiation/useShiftRateNegotiationConditions";
import { type ModalShiftData, type ModalWorkplaceData } from "../types";

/**
 * A shift is deemed bookable, i.e. we allow the worker to book it but showing the Book button if
 * 1. The bookability status is ALLOWED
 * 2. There isn't a windowing restriction on the shift
 * 3. There are no blocking requirements
 *
 * If the shift is blocked for booking because the worker needs to provide some data for background checks,
 * then we still allow them to book the shift, but collect the data before we confirm it.
 * This is done in `performChecksBeforeBooking` in `src/appV2/redesign/Shift/Booking/useBookShift.ts`
 */
function getIsBookable(params: {
  bookabilityStatus: BookabilityStatusItem | undefined;
  shift: ModalShiftData | undefined;
  blockingRequirementsContext: BlockingRequirementsContext | undefined;
}): boolean {
  const { bookabilityStatus, shift, blockingRequirementsContext } = params;
  return (
    isBookabilityAllowedWithBackgroundCheck(bookabilityStatus) &&
    isNil(shift?.attributes.windowRestriction) &&
    !blockingRequirementsContext?.hasBlockingRequirements
  );
}

/**
 * Returns `true` if the worker needs to complete something before even starting to request the shift.
 * For example: They need to complete some documents before requesting.
 *
 * This function evaluates to true only if there are unmet criteria beyond:
 * - `SHIFT_INSTANT_BOOK_DISABLED` (expected for non-instant bookable shifts)
 * - `WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK` (collected during request flow)
 */
function getNeedsCriteriaBeforeBookingRequest(params: {
  bookabilityStatus: BookabilityStatusItem | undefined;
  isInstantBookDisabled: boolean;
  isBookable: boolean;
}): boolean {
  const { bookabilityStatus, isInstantBookDisabled, isBookable } = params;

  if (!isInstantBookDisabled || isBookable) {
    return false;
  }

  const criteriaToIgnore = [
    BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
    BookabilityUnmetCriteria.WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK,
  ];

  return hasAdditionalUnmetBookabilityCriteria(bookabilityStatus, criteriaToIgnore);
}

function useRateNegotiationEnabled(params: {
  shift: ModalShiftData | undefined;
  workplace: ModalWorkplaceData | undefined;
}) {
  const { shift, workplace } = params;
  const rateNegotiationConditions = useShiftRateNegotiationConditions();

  const hasWorkerRejectedRateNegotiation = Boolean(
    shift?.rateNegotiation?.status === ShiftRateNegotiationStatus.REJECTED &&
    shift?.rateNegotiation?.lastOfferBy === ShiftRateNegotiationRole.WORKER
  );

  return (
    isDefined(shift) &&
    isDefined(workplace) &&
    isShiftRateNegotiationEnabled(
      {
        shiftStart: shift.attributes.start,
        workplaceShiftRateNegotiability:
          workplace.attributes.rateNegotiation ?? WorkplaceShiftRateNegotiability.OFF,
        workplaceId: workplace.id,
        workplaceState: workplace.attributes.location.state,
        workplaceMsa: workplace.attributes.location.msa,
        isShiftChargeUpdatedManually: shift.attributes.isChargeUpdatedManually,
      },
      rateNegotiationConditions
    ) &&
    !hasWorkerRejectedRateNegotiation
  );
}

export function useFooterData() {
  const {
    navigateToModal,
    shiftId: shiftIdFromParams,
    shift,
    bookabilityStatus,
    blockingRequirementsContext,
  } = useShiftModalsDataContext();

  const { workplace, offer } = shift ?? {};

  const shiftId = shift?.id ?? shiftIdFromParams;
  const { userId: workerId } = useDefinedWorker();
  const backgroundCheckContext = getBackgroundCheckContext(bookabilityStatus);
  const missingBackgroundCheckDataTypes = backgroundCheckContext.missingDataTypes;

  const { bookingRequestsEnabled } = useWorkplaceHasBookingRequestsEnabled(workplace?.id);

  const { data: bookingRequests } = useGetBookingRequests(
    {
      filter: {
        status: { eq: BookingRequestStatus.PENDING },
        "worker.id": { eq: workerId },
        "shift.id": { eq: shiftId ?? "" },
      },
    },
    {
      enabled: bookingRequestsEnabled && isDefined(shiftId),
    }
  );

  const hasExistingBookingRequest = (bookingRequests?.data?.length ?? 0) > 0;

  const isDocumentResolutionFlowEnabled = useCbhFlag(
    CbhFeatureFlag.BOOK_SHIFT_DOC_RESOLUTION_FLOW,
    {
      defaultValue: false,
    }
  );

  // Only show the doc-resolution flow when ALL blocking requirements are visible.
  // If any blocking docs are invisible (visibleToHCP=false), we don't take the
  // doc-resolution path because the modal can't display those requirements,
  // leaving the user unable to resolve them.
  const allBlockingRequirementsVisible =
    Boolean(blockingRequirementsContext?.hasBlockingRequirements) &&
    (blockingRequirementsContext?.invisibleBlockingRequirements?.length ?? 0) === 0;

  const hasQuizBlocker = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES,
  ]);

  const hasDocumentBlocker = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS,
  ]);

  // The doc-resolution flow is enabled when the shift is blocked only by
  // resolvable criteria: documents, the rules quiz, or both.
  // Any other unmet criteria disqualifies.
  const isBlockedByResolvableCriteria =
    (hasDocumentBlocker || hasQuizBlocker) &&
    !hasAdditionalUnmetBookabilityCriteria(bookabilityStatus, [
      ...(hasDocumentBlocker ? [BookabilityUnmetCriteria.WORKER_MISSING_REQUIREMENTS] : []),
      ...(hasQuizBlocker
        ? [BookabilityUnmetCriteria.WORKER_HAS_NOT_PASSED_QUIZ_FOR_WORKPLACE_RULES]
        : []),
    ]) &&
    isNil(shift?.attributes.windowRestriction) &&
    // When docs are blocking, all must be visible so the modal can display them.
    // When only the quiz is blocking (no docs), skip the visibility check but
    // still require blockingRequirementsContext to be defined so the modal can render.
    (hasDocumentBlocker
      ? Boolean(blockingRequirementsContext?.hasBlockingRequirements) &&
        allBlockingRequirementsVisible
      : isDefined(blockingRequirementsContext));

  const isBookable = getIsBookable({
    bookabilityStatus,
    shift,
    blockingRequirementsContext,
  });

  const isInstantBookDisabled = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.SHIFT_INSTANT_BOOK_DISABLED,
  ]);

  const needsCriteriaBeforeBookingRequest = getNeedsCriteriaBeforeBookingRequest({
    bookabilityStatus,
    isInstantBookDisabled,
    isBookable,
  });

  const isTestAccount = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.WORKER_DISABLED_TEST_ACCOUNT,
  ]);

  const isShiftAlreadyBooked = hasUnmetBookabilityCriteria(bookabilityStatus, [
    BookabilityUnmetCriteria.SHIFT_ALREADY_GRABBED,
  ]);

  const isRateNegotiationEnabled = useRateNegotiationEnabled({ shift, workplace });

  const showEnabledBookingRequestButton =
    bookingRequestsEnabled &&
    (hasExistingBookingRequest || (!needsCriteriaBeforeBookingRequest && isInstantBookDisabled));

  const showDisabledBookingRequestButton =
    bookingRequestsEnabled &&
    isInstantBookDisabled &&
    isShiftAlreadyBooked &&
    !hasExistingBookingRequest;

  const showBookingRequestButton =
    showEnabledBookingRequestButton || showDisabledBookingRequestButton;

  const shouldHideBookCta =
    (isBookable && (isInstantBookDisabled || isTestAccount)) ||
    showBookingRequestButton ||
    showDisabledBookingRequestButton;

  return {
    isBookable,
    isBlockedByResolvableCriteria,
    isDocumentResolutionFlowEnabled,
    missingBackgroundCheckDataTypes,
    isInstantBookDisabled,
    isTestAccount,
    isRateNegotiationEnabled,
    isShiftAlreadyBooked,
    shouldHideBookCta,
    shift,
    shiftId,
    workplace,
    offer,
    navigateToModal,
    showBookingRequestButton,
    hasExistingBookingRequest,
  };
}
